/**
 * Feature Flags Hook (W0.1)
 *
 * Server-resolved flags for all three portals. Modelled on `useBetaConfig` —
 * same public endpoint shape, same boot-time role — with one deliberate
 * difference: `staleTime` is 5 minutes rather than `Infinity`, because these
 * carry the kill switch and a lever nobody refetches is not a lever.
 *
 * HOW TO GATE ON A FLAG — the two forms are not interchangeable:
 *
 *   // A NEW surface this release introduces. Fail CLOSED: while the flags are
 *   // still resolving, show today's behaviour, not tomorrow's.
 *   const { flags, isResolved } = useFeatureFlags();
 *   if (isResolved && flags.MY_NEW_FLAG) { …new… }
 *
 *   // A surface that is ALREADY live in production. Fail OPEN: an outage of
 *   // this endpoint must never hide a feature users already have.
 *   if (flags.SOME_SHIPPED_FEATURE) { …existing… }
 *
 * `flags` is always fully populated — compiled defaults under whatever the
 * server returned for the keys this build knows — so a consumer never handles
 * `undefined`, and every default is `false`. A failed request therefore degrades to "the platform as
 * it behaves today", which is the rule the whole release ramp rests on.
 */

import { useQuery } from '@tanstack/react-query';
import { FEATURE_FLAGS, type FeatureFlagsPayload, type ResolvedFlags } from '@tonnie/foundation';
import { useApiClient } from '../providers/SdkProvider';
import { useAuth } from '../providers/AuthProvider';

export const featureFlagKeys = {
  all: ['featureFlags'] as const,
  resolved: () => [...featureFlagKeys.all, 'resolved'] as const,
};

export interface UseFeatureFlagsResult {
  /** Every key, always. Defaults (all false) under the server's answer. */
  flags: ResolvedFlags;
  /** True once the server has answered. Gate NEW surfaces on this. */
  isResolved: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function useFeatureFlags(): UseFeatureFlagsResult {
  const client = useApiClient();
  // Clerk (or the mobile auth bridge) resolves `isLoaded` asynchronously on
  // every fresh mount. Without gating on it, this query fires immediately and
  // races that resolution: `client`'s token provider reads `isSignedIn` off a
  // ref that is still `false` at call time, so the request goes out with NO
  // Authorization header, resolves only the global/tenant flag layers (never
  // the user layer), and — because `staleTime` is 5 minutes — that
  // wrongly-scoped answer then sticks for the rest of the session instead of
  // being corrected once the real session is known. Every other auth-scoped
  // SDK hook already waits on `isSignedIn && !!token` (see useJourney.ts,
  // useHeroes.ts, useCare.ts); this hook must ALSO stay usable signed-out
  // (same public, boot-time role as `useBetaConfig`), so it waits only on
  // `isLoaded` — Clerk settling either way — not on being signed in.
  const { isLoaded } = useAuth();

  const query = useQuery<FeatureFlagsPayload, Error>({
    queryKey: featureFlagKeys.resolved(),
    queryFn: () => client.get<FeatureFlagsPayload>('/api/config/flags'),
    enabled: isLoaded,
    // Short enough that flipping a flag reaches open sessions without a reload;
    // long enough not to refetch on every mount.
    staleTime: 5 * 60 * 1000,
    gcTime: Infinity,
    retry: 1,
  });

  return {
    // The server can only override keys it actually returned AND this build
    // knows; anything it omits keeps its compiled default, and a key from a
    // newer server is dropped rather than typed as something it is not.
    flags: FEATURE_FLAGS.resolve([FEATURE_FLAGS.sanitize(query.data?.flags)]),
    isResolved: query.isSuccess,
    isLoading: query.isLoading,
    error: query.error ?? null,
  };
}
